import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { ROUTES } from "../consts/routes.consts";
import brandLogo from "../../assets/images/svg/white-logo-new.svg";
import { useMetaBuilderDisconnectMutation } from "../apis/metaBuilderApi";
import { isMetaAdsTokenExpired, clearMetaAdsAuthFromStorage } from "../../utils/api.util";

/**
 * Simple Custom Navbar Component for Meta Ads pages
 * - Left: PitchPlaylist logo (clickable, goes to home)
 * - Right: Terms of Services | Meta Ads Builder | Logout (when logged in)
 * - Optional: showDivider prop to control bottom border visibility
 */
interface MetaAdsNavbarProps {
  showDivider?: boolean;
}

const MetaAdsNavbar: React.FC<MetaAdsNavbarProps> = ({
  showDivider = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [metaBuilderDisconnect] = useMetaBuilderDisconnectMutation();

  // Check login state on mount and when localStorage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      if (isMetaAdsTokenExpired()) {
        setIsLoggedIn(false);
        return;
      }
      const token = localStorage.getItem("metaAdsPublicToken");
      setIsLoggedIn(!!token);
    };

    // Check initial status
    checkLoginStatus();

    // Listen for storage changes (for logout in other tabs)
    const handleStorageChange = () => {
      checkLoginStatus();
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await metaBuilderDisconnect().unwrap();
    } catch (error: any) {
      console.error("Disconnect API error:", error);
    } finally {
      clearMetaAdsAuthFromStorage();
      setIsLoggedIn(false);
      toast.success("Logged out successfully!");

      // Navigate to login page
      navigate(ROUTES.META_ADS_LOGIN);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-[100] mt-2">
      <div
        className="relative mx-auto"
        style={{
          //maxWidth: "1400px",
          paddingLeft: "100px",
          paddingRight: "100px",
        }}
      >
        <div className="flex items-center justify-between px-6 py-4">
          {/* Divider - positioned at bottom with same padding */}
          {showDivider && (
            <div
              className="absolute bottom-0 h-px"
              style={{
                left: "100px",
                right: "100px",
                backgroundColor: "#FFFFFF33",
              }}
            />
          )}
          {/* Left: Logo */}
          <div
            className="flex items-center cursor-pointer"
           // onClick={() => navigate(ROUTES.HOME)}
          >
            <img src={brandLogo} alt="PitchPlaylist" className="h-10 w-auto" />
          </div>

          {/* Right: Links */}
          <div className="flex items-center gap-[50px]">
            <button
              onClick={() => navigate(ROUTES.META_PRIVACY_TERMS)}
              className="text-[#E4E4E4] text-[16px] font-medium"
            >
              Terms of Services
            </button>
            <button
              onClick={() => navigate(ROUTES.META_ADS_SETUP_PUBLIC)}
              className="text-[#E4E4E4] text-[16px] font-bold "
            >
              Meta Ads Builder
            </button>
            {isLoggedIn && location.pathname !== ROUTES.META_ADS_LOGIN && (
              <button
                onClick={handleLogout}
                className="text-[#58F2A9] text-[16px] font-bold"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default MetaAdsNavbar;
